import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useBuilderStore } from '@/components/builder/utils/store'
import appStore from '@/stores/app'

import { AGENT_PARAM, ERRORS, LIMITS, STORAGE_KEY, createError, parseError } from '../protocol'
import { withTimeout } from '../timers'
import { close as closePairing, createLongPollingTransport, pair as pairTab } from '../transport/longPolling'

const DISCONNECTED_HIDE_DELAY = 5000
const READY_POLL = 100

const readStorage = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null')
  } catch (_error) {
    return null
  }
}

const writeStorage = value => {
  if (value) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } else {
    sessionStorage.removeItem(STORAGE_KEY)
  }
}

const waitUntilReady = (isReady, timeout) => new Promise(resolve => {
  const started = Date.now()

  const check = () => {
    if (isReady() || Date.now() - started > timeout) {
      resolve(isReady())
      return
    }
    setTimeout(check, READY_POLL)
  }

  check()
})

const batchesOf = command => command.batches || [{ ops: command.ops }]

const opsOf = command => batchesOf(command).reduce((total, batch) => total + (batch.ops?.length || 0), 0)

const takesScreenshot = command => Boolean(command.screenshot) || batchesOf(command).some(batch => (batch.ops || []).some(op => op?.op === 'screenshot'))

const timeoutOf = command => {
  const screenshot = takesScreenshot(command) ? LIMITS.screenshotTimeout : 0
  return LIMITS.commandTimeout + opsOf(command) * LIMITS.commandTimeoutPerOp + screenshot
}

const countBatches = result => (result.batches || []).reduce((counts, batch) => ({ ...counts, [batch.status]: (counts[batch.status] || 0) + 1 }), {})

const summarize = (command, result) => ({
  ops: opsOf(command),
  applied: result.result?.applied || 0,
  ok: result.ok,
  batches: countBatches(result),
  touched: result.result?.outline?.root || null,
  added: result.diff?.added?.length || 0,
  changed: result.diff?.changed?.length || 0,
  removed: result.diff?.removed?.length || 0,
  error: result.error?.code || null,
})

export default function useAgentPairing(runtime) {
  const { search } = useLocation()
  const { id: postIdParam } = useParams()
  const postId = Number(postIdParam)
  const siteId = appStore.site?.id
  const code = useMemo(() => new URLSearchParams(search).get(AGENT_PARAM), [search])
  const { postBuilder: { version: { item: currentVersion, setItem: setVersion } } } = useBuilderStore()

  const [status, setStatus] = useState('off')
  const [pairing, setPairing] = useState(null)
  const [error, setError] = useState(null)
  const [lastCommand, setLastCommand] = useState(null)

  const controllerRef = useRef(null)
  const hideTimerRef = useRef(null)
  const runtimeRef = useRef(runtime)
  const versionRef = useRef({ currentVersion, setVersion })

  runtimeRef.current = runtime
  versionRef.current = { currentVersion, setVersion }

  const stopLoop = useCallback(() => {
    controllerRef.current?.abort()
    controllerRef.current = null
  }, [])

  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(() => setStatus(current => current === 'disconnected' ? 'off' : current), DISCONNECTED_HIDE_DELAY)
  }, [])

  const handleCommand = useCallback(async command => {
    const { current } = runtimeRef
    let result

    try {
      if (command.focus !== undefined) {
        current.setFocus(command.focus)
      }

      await waitUntilReady(() => current.isReady(), LIMITS.readyTimeout)

      const timeoutMs = timeoutOf(command)
      result = await withTimeout(
        current.execute(command),
        timeoutMs,
        () => createError(ERRORS.TIMEOUT, `The command did not finish within ${Math.round(timeoutMs / 1000)}s`, { timeoutMs }),
      )
    } catch (caught) {
      result = current.failure(caught)
    }

    setLastCommand({ id: command.id, ok: result.ok, at: Date.now(), error: result.error?.message || null })

    return {
      ...result,
      aliases: Object.fromEntries(current.aliases),
      summary: summarize(command, result),
    }
  }, [])

  const startLoop = useCallback(pairingId => {
    stopLoop()

    const controller = new AbortController()
    controllerRef.current = controller
    setStatus('connected')

    createLongPollingTransport({ pairingId }).run({
      signal: controller.signal,
      onCommand: handleCommand,
      onStatus: (next, info = {}) => {
        if (controller.signal.aborted) {
          return
        }

        if (next === 'disconnected') {
          writeStorage(null)
          setPairing(null)
          scheduleHide()
        }

        setError(info.error ? parseError(info.error) : null)
        setStatus(next)
      },
    })
  }, [stopLoop, handleCommand, scheduleHide])

  const adopt = useCallback((response, pairCode) => {
    const entry = { pairingId: response.pairingId, code: pairCode, postId, siteId, pairedAt: Date.now() }
    const { current } = runtimeRef

    writeStorage(entry)
    setPairing(entry)

    if (response.focus !== undefined) {
      current.setFocus(response.focus)
    }

    Object.entries(response.aliases || {}).forEach(([alias, id]) => current.aliases.set(alias, id))

    const { currentVersion: active, setVersion: adoptVersion } = versionRef.current
    if (response.version && response.version.id !== active?.id) {
      adoptVersion(response.version)
    }

    startLoop(entry.pairingId)
  }, [postId, siteId, startLoop])

  const connect = useCallback(async pairCode => {
    setStatus('pairing')
    setError(null)

    try {
      const response = await pairTab({ code: pairCode, siteId, postId })
      adopt(response, pairCode)
    } catch (caught) {
      setError(parseError(caught))
      setStatus('error')
    }
  }, [adopt, siteId, postId])

  useEffect(() => {
    if (!postId || !siteId) {
      return
    }

    const stored = readStorage()
    const sameTab = stored && stored.postId === postId && stored.siteId === siteId
    const pairCode = code || (sameTab ? stored.code : null)

    if (pairCode) {
      connect(pairCode)
    }
  }, [code, postId, siteId, connect])

  useEffect(() => () => {
    stopLoop()
    clearTimeout(hideTimerRef.current)
  }, [stopLoop])

  const disconnect = useCallback(async () => {
    const current = pairing

    stopLoop()
    writeStorage(null)
    setPairing(null)
    setError(null)
    setStatus('disconnected')
    scheduleHide()

    if (current?.pairingId) {
      try {
        await closePairing(current.pairingId)
      } catch (_error) {
        return
      }
    }
  }, [pairing, stopLoop, scheduleHide])

  const retry = useCallback(() => {
    const stored = readStorage()
    const pairCode = code || stored?.code
    if (pairCode) {
      connect(pairCode)
    }
  }, [code, connect])

  return useMemo(() => ({
    status, pairing, error, lastCommand, disconnect, retry,
  }), [status, pairing, error, lastCommand, disconnect, retry])
}
